import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Backdrop,
  ButtonPrimary,
  ButtonTertiary,
  DialogConfirmation,
  FormCheckbox,
  Type,
} from '@spotify-internal/encore-creator-web';
import { kleinBlue } from '@spotify-internal/encore-foundation';
import { Logger, useError } from '@mrkt/features/Platform';

import { getTermsUrl } from '.';
import { useShouldShowTerms } from './hooks/useShouldShowTerms';
import { WEBGATE_DOMAIN, webgateFetch } from '../../shared/lib/api';
import { ActionType, BannerType, useBannerState } from '../Banner/BannerState';
import { logout } from '@mrkt/features/auth';

type Props = {
  children: React.ReactElement;
};

export const ACCEPT_TERMS_URL = `${WEBGATE_DOMAIN}/s4x-me/v0/accept_terms`;

const StyledLink = styled.a`
  color: ${({ theme }) =>
    (theme && theme.colors && theme.colors.primaryColor) || kleinBlue};
`;

const Fallback = ({ children }: { children: React.ReactElement }) => {
  // get the actual exception that was thrown
  const [error] = useError();

  // get a reference to the global banner for mrkt-web
  const [, dispatch] = useBannerState();

  // execute this function once the fallback component mounts in the DOM.
  // Show the error banner to the user.
  React.useEffect(() => {
    dispatch({
      type: ActionType.SHOW,
      message: "Something went wrong. We'll ask again later.",
      bannerType: BannerType.ERROR,
      bannerOptions: {
        compact: false,
        dismissOnRouteChange: true,
        showDismissButton: false,
      },
    });
  }, [dispatch, error.message]);

  return children;
};

export function Terms(props: Props) {
  const [submitIsDisabled, setSubmitIsDisabled] = useState(true);
  const [isError, setIsError] = useState(false);

  // Render children if don't need to accept terms.
  if (!useShouldShowTerms()) {
    return props.children;
  }

  if (isError) {
    return (
      // Render the original page, but include an error banner.
      <Fallback>{props.children}</Fallback>
    );
  }

  const toggleAcceptTerms = () => {
    setSubmitIsDisabled(!submitIsDisabled);
  };

  async function onAcceptTerms() {
    setSubmitIsDisabled(true);

    try {
      const response = await webgateFetch(ACCEPT_TERMS_URL, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Submitting to accept terms failed');
      }

      window.location.reload();
    } catch (e) {
      Logger.logError('s4a-accept-terms', e);
      setIsError(true);
    }
  }

  const homepageUrl = `${window.location.protocol}//${window.location.host}`;

  return (
    <Backdrop center data-testid="terms">
      <DialogConfirmation
        dialogTitle="We updated our terms and conditions"
        body={
          <Type as="p">
            To continue using Spotify for Artists, please accept&nbsp;
            <StyledLink
              href={getTermsUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              the updated terms
            </StyledLink>
            .
          </Type>
        }
        footer={
          <>
            <ButtonTertiary
              buttonSize={ButtonTertiary.sm}
              condensed
              onClick={() => logout(homepageUrl)}
            >
              Log Out
            </ButtonTertiary>
            <ButtonPrimary
              condensed
              data-testid="terms-submit"
              disabled={submitIsDisabled}
              buttonSize={ButtonPrimary.sm}
              onClick={() => onAcceptTerms()}
            >
              I Accept
            </ButtonPrimary>
          </>
        }
        legal={
          <FormCheckbox
            data-testid="terms-accept-checkbox"
            onClick={() => toggleAcceptTerms()}
            small
          >
            I have read and agree to the terms and conditions.
          </FormCheckbox>
        }
        legalStrict
      />
    </Backdrop>
  );
}
